import {
  Notification,
  notificationsAtom,
} from "@app/shared/atoms/notifications-atom";
import { useIsLoggedIn } from "@framework/account/hooks";
import { useDirection } from "@framework/shared/hooks";
import { useNavigate } from "@remix-run/react";
import { useEffect, useState } from "react";
import { resolveNotificationLink } from "../utils/notification-link";
import { useNotifications } from "./use-notifications";

export function useNotificationsBell() {
  const [open, setOpen] = useState(false);
  const [replyNotification, setReplyNotification] =
    useState<Notification | null>(null);
  const direction = useDirection();
  const navigate = useNavigate();
  const isLoggedIn = useIsLoggedIn();
  const { unreadCount, notifications } = notificationsAtom.useValue();
  const {
    clearAll,
    deleteNotification,
    loadNotifications,
    markAllAsRead,
    markAsRead,
    seedUnreadCount,
  } = useNotifications();

  useEffect(() => {
    if (isLoggedIn) seedUnreadCount();
  }, [isLoggedIn, seedUnreadCount]);

  useEffect(() => {
    if (open) loadNotifications();
  }, [loadNotifications, open]);

  useEffect(() => {
    if (!isLoggedIn || !("serviceWorker" in navigator)) return;

    const handlePushMessage = (event: MessageEvent) => {
      if (event.data?.type !== "mentor-me:push-notification") return;

      seedUnreadCount();
      if (open) loadNotifications();
    };

    navigator.serviceWorker.addEventListener("message", handlePushMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handlePushMessage);
    };
  }, [isLoggedIn, loadNotifications, open, seedUnreadCount]);

  const openNotification = (notification: Notification) => {
    markAsRead(notification);
    setOpen(false);

    if (notification.type === "contactReplied") {
      setReplyNotification(notification);
      return;
    }

    const link = resolveNotificationLink(notification);

    if (link) navigate(link);
  };

  return {
    clearAll,
    deleteNotification,
    direction,
    isLoggedIn,
    loadNotifications,
    markAllAsRead,
    markAsRead,
    notifications,
    open,
    openNotification,
    recentNotifications: notifications.slice(0, 8),
    replyNotification,
    setOpen,
    setReplyNotification,
    seedUnreadCount,
    unreadCount,
  };
}
